import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Avatar, List, Dropdown, theme } from "antd";
import {
  ArrowLeftOutlined,
  SendOutlined,
  UserOutlined,
  LockOutlined,
  MoreOutlined
} from "@ant-design/icons";
import { IoNotificationsOffOutline, IoInformationCircleOutline } from "react-icons/io5";
import { IoIosCheckboxOutline, IoMdCloseCircleOutline } from "react-icons/io";
import { GrFavorite } from "react-icons/gr";
import { MdBlock } from "react-icons/md";
import { FiThumbsDown, FiMinusCircle } from "react-icons/fi";
import { VscClearAll } from "react-icons/vsc";
import { RiChatDeleteLine } from "react-icons/ri";
import "./ChatWindow.css";
import FileUpload from "../FileUpload/FileUpload";
import { Bubble } from "@ant-design/x";

import { useTranslation } from "react-i18next";
const { TextArea } = Input;

// Placeholder conversation menu (no actions wired up yet).
const menuItems = t => [
  { key: "0", icon: <IoInformationCircleOutline />, label: t("Contact info") },
  { key: "1", icon: <IoIosCheckboxOutline />, label: t("Select messages") },
  { key: "2", icon: <IoNotificationsOffOutline />, label: t("Mute notification") },
  { key: "3", icon: <RiChatDeleteLine />, label: t("Disappearing messages") },
  { key: "4", icon: <GrFavorite />, label: t("Add to favorites") },
  { key: "5", icon: <IoMdCloseCircleOutline />, label: t("Close chats") },
  { type: "divider" },
  { key: "6", icon: <FiThumbsDown />, label: t("Report") },
  { key: "7", icon: <MdBlock />, label: t("Block") },
  { key: "8", icon: <FiMinusCircle />, label: t("Clear chat") },
  { key: "9", icon: <VscClearAll />, label: t("Delete chat") }
];

const ChatWindow = ({ receiver, messages, sender, onSendMessage, onBack }) => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const [input, setInput] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    // Scroll only the message list, not the page around it.
    const list = messagesEndRef.current;
    if (list) list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  };

  const sendMessage = () => {
    if (input === "") return;
    onSendMessage(input, uploadedFile);
    setInput("");
    setUploadedFile(null);
  };

  return (
    <div className="chat-thread" style={{ "--chat-online": token.colorSuccessText }}>
      <div className="chat-thread__header">
        {onBack && (
          <Button
            type="text"
            className="chat-thread__back"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            aria-label={t("Back")}
          />
        )}
        <Avatar
          size={40}
          className="chat-thread__avatar"
          src={receiver?.picture?.url}
          icon={!receiver?.picture?.url && <UserOutlined />}
        />
        <div className="chat-thread__who">
          <div className="chat-thread__name">{receiver?.firstName}</div>
          <div className="chat-thread__status">{t("Online")}</div>
        </div>
        <Dropdown menu={{ items: menuItems(t) }} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} aria-label={t("More options")} />
        </Dropdown>
      </div>
      <div className="chat-thread__messages" ref={messagesEndRef}>
        <div className="chat-thread__notice">
          <Bubble
            className="chat-thread__encrypted"
            content={
              <span>
                <LockOutlined />{" "}
                {t(
                  "Messages and calls are end-to-end encrypted. Only people in this chat can read , listen to, or share them. learn more"
                )}
              </span>
            }
          />
          <Bubble className="chat-thread__date" content={t("Today")} />
        </div>
        {messages?.length > 0 && (
          <List
            itemLayout="horizontal"
            dataSource={messages}
            split={false}
            renderItem={message => (
              <Bubble
                className="chat-thread__bubble"
                placement={`${message?.sender === sender?._id ? "start" : "end"}`}
                content={message?.message}
                avatar={{
                  icon: <UserOutlined />,
                  className:
                    message?.sender === sender?._id
                      ? "chat-thread__avatar--me"
                      : "chat-thread__avatar--them"
                }}
              />
            )}
          />
        )}
      </div>
      <div className="chat-thread__composer">
        <TextArea
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={t("Type your message here")}
          aria-label={t("Type your message here")}
          autoSize={{ minRows: 1, maxRows: 5 }}
          enterKeyHint="send"
        />
        <FileUpload value={uploadedFile} updateFileFun={val => setUploadedFile(val)} />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={sendMessage}
          aria-label={t("Send")}
          className="chat-thread__send"
        >
          <span className="chat-thread__send-label">{t("Send")}</span>
        </Button>
      </div>
    </div>
  );
};

export default ChatWindow;
